// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { exportAndUploadFrame } from '../../../../services/frameExport'
import { addNode, mergeSessionEntities } from '../../../../store/slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../../../../store/slices/selectionSlice'
import { useFrameEditState } from './useFrameEditState'
import type { Rect, CanvasItemData, Edge, Corner, LayerItemData } from './types'

interface UseFramingParams {
    brushEnabled: boolean
    worldRectsById: Record<string, Rect>
    initialWorldRectsById: Record<string, Rect>
    setWorldRects: (rects: Record<string, Rect>) => void
    worldRects: Array<{ id: string; src: string; mediaType?: string; rect: Rect }>
    imageRectInContainer: Rect | null
    imageNaturalSize: { w: number; h: number } | null
    imageSizesBySrc: Record<string, { w: number; h: number } | undefined>
    selectedNodeIds: string[]
    sessionId?: string
    dispatch: any
    containerRef: React.RefObject<HTMLDivElement | null>
    clientToWorld: (clientX: number, clientY: number) => { x: number; y: number } | null
    getDrawOverlayForId?: (id: string) => Promise<Blob | null>
    hasDrawingChangesForId?: (id: string) => boolean
    frameModeById: Record<string, boolean>
}

export function useFraming(params: UseFramingParams) {
    const {
        brushEnabled,
        worldRectsById,
        initialWorldRectsById,
        setWorldRects,
        worldRects,
        imageNaturalSize,
        imageSizesBySrc,
        selectedNodeIds,
        sessionId,
        dispatch,
        containerRef,
        clientToWorld,
    } = params

    const [framingIds, setFramingIds] = useState<Set<string>>(() => new Set())
    const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null)
    const [confirmingIds, setConfirmingIds] = useState<Set<string>>(() => new Set())
    const [activeLayerByFrameId, setActiveLayerByFrameId] = useState<Record<string, string>>({})

    const frameResizeRef = useRef<{ id: string; edge: Edge; start: { x: number; y: number }; startRect: Rect } | null>(null)
    const frameMoveRef = useRef<{ id: string; start: { x: number; y: number }; startRect: Rect } | null>(null)
    
    // Updated to track specific layer
    const layerMoveRef = useRef<{ frameId: string; layerId: string; start: { x: number; y: number }; startRect: Rect } | null>(null)
    const layerResizeRef = useRef<{ frameId: string; layerId: string; corner: Corner; start: { x: number; y: number }; startRect: Rect; constrainAspect: boolean; resizeFromCenter: boolean } | null>(null)

    // Frame edit state management
    const frameEditHook = useFrameEditState({
        worldRectsById,
        initialWorldRectsById,
        setWorldRects,
        worldRects,
    })

    const setActiveLayer = useCallback((frameId: string, layerId: string) => {
        setActiveLayerByFrameId((prev) => {
            if (prev[frameId] === layerId) return prev
            return { ...prev, [frameId]: layerId }
        })
    }, [])

    useEffect(() => {
        // Keep active layer valid as layers are added/removed/reordered.
        // Default to the top-most layer (highest zIndex).
        setActiveLayerByFrameId((prev) => {
            let changed = false
            const next: Record<string, string> = { ...prev }

            for (const frameId of Object.keys(frameEditHook.frameEditState)) {
                const edit = frameEditHook.frameEditState[frameId]
                if (!edit || edit.layers.length === 0) continue

                const topMost = [...edit.layers].sort((a, b) => b.zIndex - a.zIndex)[0]
                const current = prev[frameId]
                const isValid = current
                    ? edit.layers.some((l) => l.id === current)
                    : false

                if (!isValid && topMost?.id) {
                    next[frameId] = topMost.id
                    changed = true
                }
            }

            return changed ? next : prev
        })
    }, [frameEditHook.frameEditState])

    const enableFrameFor = useCallback((id: string) => {
        setFramingIds((prev) => {
            if (prev.has(id)) return prev
            const next = new Set(prev)
            next.add(id)
            return next
        })
        frameEditHook.enableEdit(id)
    }, [frameEditHook])

    const disableFrameFor = useCallback((id: string) => {
        setFramingIds((prev) => {
            if (!prev.has(id)) return prev
            const next = new Set(prev)
            next.delete(id)
            return next
        })
        frameEditHook.disableEdit(id)
    }, [frameEditHook])

    const revertFrameFor = useCallback((id: string) => {
        frameEditHook.revertEdit(id)
    }, [frameEditHook])

    const fitFrameFor = useCallback((id: string) => {
        frameEditHook.fitFrameToContent(id)
    }, [frameEditHook])

    const confirmFrameFor = useCallback(async (id: string) => {
        if (!sessionId) return
        const edit = frameEditHook.frameEditState[id]
        if (!edit) return
        
        if (edit.layers.length === 0) return

        // Set loading state
        setConfirmingIds((prev) => new Set(prev).add(id))

        try {
            // Build layer export data
            const layersToExport = await Promise.all(edit.layers.map(async (layer) => {
                const natural = imageSizesBySrc[layer.src] || imageNaturalSize
                if (!natural) throw new Error(`Missing natural size for layer ${layer.src}`)
                
                // Check for drawing overlay
                let overlayBlob: Blob | null = null
                if (typeof params.getDrawOverlayForId === 'function') {
                    if (layer.id === id) {
                        overlayBlob = await params.getDrawOverlayForId(id)
                    }
                }

                return {
                    id: layer.id,
                    sourceUrl: layer.src,
                    imageRect: layer.currentRect,
                    frameRect: edit.currentFrame,
                    imageNaturalSize: natural,
                    initialImageRect: layer.baseRect,
                    zIndex: layer.zIndex,
                    overlayBlob,
                }
            }))

            // Use shared export service
            const { node, commit } = await exportAndUploadFrame({
                sessionId,
                frameRect: edit.currentFrame,
                layers: layersToExport,
                commitMessage: 'Edited: frame resize',
                baseNodeIds: [id],
            })

            // Update Redux state
            dispatch(addNode({ sessionId, node }))
            dispatch(mergeSessionEntities({ sessionId, commits: [commit] }))
            dispatch(setSelectedNodes({ sessionId, nodeIds: [node.id] }))
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Canvas] export frame failed', err)
        } finally {
            // Clear loading state first, then disable frame (order matters for overlay visibility)
            setConfirmingIds((prev) => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            disableFrameFor(id)
        }
    }, [sessionId, frameEditHook.frameEditState, imageSizesBySrc, imageNaturalSize, dispatch, disableFrameFor, params.getDrawOverlayForId])

    const frameItems: CanvasItemData[] = useMemo(() => {
        if (brushEnabled) return []
        return worldRects.map(({ id, src, mediaType, rect }) => {
            const frameEnabled = framingIds.has(id)
            const edit = frameEditHook.frameEditState[id]
            const isFrameMode = params.frameModeById[id]

            // Determine Frame Rect
            // - Object Mode: frame matches image/content exactly (conceptually locked)
            // - Frame Mode: frame is independent (user edits apply)
            //
            // IMPORTANT: We render a frameClip container by default for conceptual coherence.
            // So we always provide a frame rect; in Object Mode it is simply the image rect.
            const frameRectToUse = (isFrameMode && edit) ? edit.currentFrame : rect

            // Build Layers
            let layers: LayerItemData[] = []
            if (edit) {
                layers = edit.layers.map(l => {
                    // Logic for layer rect visibility
                    // With syncFrameRect, edit.currentRect is always up to date.
                    
                    return {
                        id: l.id,
                        src: l.src,
                        mediaType: l.mediaType,
                        rect: l.currentRect, // Always use currentRect as it is synced
                        zIndex: l.zIndex,
                        initialRect: l.baseRect
                    }
                })
            } else {
                // Default single layer state
                layers = [{
                    id,
                    src,
                    mediaType,
                    rect: rect,
                    zIndex: 0,
                    initialRect: initialWorldRectsById[id]
                }]
            }

            const activeLayerId =
                activeLayerByFrameId[id] &&
                layers.some((l) => l.id === activeLayerByFrameId[id])
                    ? activeLayerByFrameId[id]
                    : [...layers].sort((a, b) => b.zIndex - a.zIndex)[0]?.id

            // Get change info from frame edit state
            const changeInfo = frameEditHook.getChangeInfo(id)
            const hasFrameChanges = changeInfo.hasChanges
            const frameDiffersFromImage = changeInfo.frameDiffersFromImage
            const hasDrawingChanges = typeof params.hasDrawingChangesForId === 'function' ? params.hasDrawingChangesForId(id) : false
            const isConfirming = confirmingIds.has(id)

            const sizeText = (() => {
                if (!frameEnabled) return undefined
                const natural = imageSizesBySrc[src] || null
                if (!natural || !frameRectToUse) return undefined

                // Calculate world-to-natural scale from base (initial) placement
                const baseRect = initialWorldRectsById[id] || (edit?.baseFrame || rect)
                const worldToNaturalX = baseRect.w !== 0 ? natural.w / baseRect.w : 1
                const worldToNaturalY = baseRect.h !== 0 ? natural.h / baseRect.h : 1

                const wPx = Math.round(frameRectToUse.w * worldToNaturalX)
                const hPx = Math.round(frameRectToUse.h * worldToNaturalY)
                return `${wPx} × ${hPx}`
            })()

            return {
                id,
                frameRect: frameRectToUse,
                frameSizeText: sizeText,
                layers,
                hasFrameChanges,
                hasDrawingChanges,
                frameDiffersFromImage,
                isConfirming,
                activeLayerId,
                onFrameFit: frameEnabled ? () => fitFrameFor(id) : undefined,
                onFrameConfirm: frameEnabled ? () => confirmFrameFor(id) : undefined,
                isNodeSelected: selectedNodeIds.includes(id),
            }
        })
    }, [
        brushEnabled,
        worldRects,
        frameEditHook,
        framingIds,
        confirmingIds,
        imageSizesBySrc,
        selectedNodeIds,
        revertFrameFor,
        fitFrameFor,
        confirmFrameFor,
        initialWorldRectsById,
        params.frameModeById,
        activeLayerByFrameId,
    ])

    // Auto-enable framing for newly selected nodes
    useEffect(() => {
        selectedNodeIds.forEach((id) => enableFrameFor(id))
    }, [selectedNodeIds, enableFrameFor])

    // Track previous world rects to detect external changes
    const prevWorldRectsRef = useRef<Record<string, Rect>>({})

    // Sync frame edit state when layout updates worldRects
    useEffect(() => {
        Object.keys(frameEditHook.frameEditState).forEach((id) => {
            const worldRect = worldRectsById[id]
            const prevWorldRect = prevWorldRectsRef.current[id]
            const edit = frameEditHook.frameEditState[id]

            if (worldRect && edit) {
                const worldRectChanged = !prevWorldRect ||
                    worldRect.x !== prevWorldRect.x ||
                    worldRect.y !== prevWorldRect.y ||
                    worldRect.w !== prevWorldRect.w ||
                    worldRect.h !== prevWorldRect.h

                // Basic check if this is the "primary" layer's update
                // Assuming id matches for primary layer
                const primaryLayer = edit.layers.find(l => l.id === id)
                
                if (primaryLayer) {
                    const isFirstRealLayout =
                        primaryLayer.baseRect.x === primaryLayer.currentRect.x &&
                        primaryLayer.baseRect.w === primaryLayer.currentRect.w 
                        // Simplified check

                    if (isFirstRealLayout) {
                         const needsUpdate =
                            Math.abs(primaryLayer.baseRect.x - worldRect.x) > 0.01 ||
                            Math.abs(primaryLayer.baseRect.w - worldRect.w) > 0.01
                        
                        if (needsUpdate) {
                            frameEditHook.updateBasePositions(id, worldRect)
                        }
                    } else if (!params.frameModeById[id]) {
                        // Object Mode: Force Frame to match Image
                         if (worldRectChanged) {
                            // Use syncFrameRect: Moves Frame to new worldRect and shifts all layers
                            frameEditHook.syncFrameRect(id, worldRect)
                            // Note: fitFrameToContent might be redundant or conflicting if we just moved the frame
                            // frameEditHook.fitFrameToContent(id) 
                        }
                    } else {
                        // Frame Mode:
                        // If worldRect changed externally (layout/undo), we should likely still respect it
                        // as a Frame move.
                        if (worldRectChanged) {
                            frameEditHook.syncFrameRect(id, worldRect)
                        }
                    }
                }
            }
        })
        prevWorldRectsRef.current = worldRectsById
    }, [worldRectsById, frameEditHook, params.frameModeById])

    const resetFraming = useCallback(() => {
        setFramingIds(new Set())
        frameEditHook.resetAll()
    }, [frameEditHook])

    // Frame pointer handlers
    const startResizeFrame = useCallback((id: string, edge: Edge, e: React.PointerEvent) => {
        e.stopPropagation()
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        setSelectedFrameId(id)
        const edit = frameEditHook.frameEditState[id]
        if (!edit) return
        
        frameResizeRef.current = { id, edge, start: w, startRect: { ...edit.currentFrame } }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, frameEditHook.frameEditState, containerRef])

    const startMoveFrame = useCallback((id: string, e: React.PointerEvent) => {
        e.stopPropagation()
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        setSelectedFrameId(id)
        const edit = frameEditHook.frameEditState[id]
        if (!edit) return

        frameMoveRef.current = { id, start: w, startRect: { ...edit.currentFrame } }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, frameEditHook.frameEditState, containerRef])

    const startMoveLayer = useCallback((frameId: string, layerId: string, e: React.PointerEvent) => {
        e.stopPropagation()
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        const edit = frameEditHook.frameEditState[frameId]
        if (!edit) return
        
        const layer = edit.layers.find(l => l.id === layerId)
        if (!layer) return

        layerMoveRef.current = { frameId, layerId, start: w, startRect: { ...layer.currentRect } }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, frameEditHook.frameEditState, containerRef])

    const startResizeLayer = useCallback((frameId: string, layerId: string, corner: Corner, e: React.PointerEvent) => {
        e.stopPropagation()
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        const edit = frameEditHook.frameEditState[frameId]
        if (!edit) return
        
        const layer = edit.layers.find(l => l.id === layerId)
        if (!layer) return

        layerResizeRef.current = {
            frameId,
            layerId,
            corner,
            start: w,
            startRect: { ...layer.currentRect },
            constrainAspect: !!(e as any).shiftKey,
            resizeFromCenter: !!(e as any).altKey,
        }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, frameEditHook.frameEditState, containerRef])

    const onFramePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const w = clientToWorld(e.clientX, e.clientY)
        
        // Frame resize
        if (frameResizeRef.current) {
            const info = frameResizeRef.current
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y
            const r0 = info.startRect
            let nx = r0.x, ny = r0.y, nw = r0.w, nh = r0.h
            
            if (info.edge === 'n') { ny = r0.y + dy; nh = r0.h - dy }
            if (info.edge === 's') { nh = r0.h + dy }
            if (info.edge === 'w') { nx = r0.x + dx; nw = r0.w - dx }
            if (info.edge === 'e') { nw = r0.w + dx }
            nw = Math.max(10, nw)
            nh = Math.max(10, nh)
            requestAnimationFrame(() => {
                frameEditHook.updateFrameRect(info.id, { x: nx, y: ny, w: nw, h: nh })
            })
            return true
        }
        // Frame move
        if (frameMoveRef.current) {
            const info = frameMoveRef.current
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y
            const r0 = info.startRect
            requestAnimationFrame(() => {
                frameEditHook.updateFrameRect(info.id, { x: r0.x + dx, y: r0.y + dy, w: r0.w, h: r0.h })
            })
            return true
        }
        // Layer move
        if (layerMoveRef.current) {
            const info = layerMoveRef.current
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y
            const r0 = info.startRect
            requestAnimationFrame(() => {
                frameEditHook.updateLayerRect(info.frameId, info.layerId, { x: r0.x + dx, y: r0.y + dy, w: r0.w, h: r0.h })
            })
            return true
        }
        // Layer resize
        if (layerResizeRef.current) {
            const info = layerResizeRef.current
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y
            const r0 = info.startRect
            let nx = r0.x, ny = r0.y, nw = r0.w, nh = r0.h

            if (info.corner === 'nw') { nx = r0.x + dx; ny = r0.y + dy; nw = r0.w - dx; nh = r0.h - dy }
            else if (info.corner === 'ne') { ny = r0.y + dy; nw = r0.w + dx; nh = r0.h - dy }
            else if (info.corner === 'sw') { nx = r0.x + dx; nw = r0.w - dx; nh = r0.h + dy }
            else if (info.corner === 'se') { nw = r0.w + dx; nh = r0.h + dy }
            else if (info.corner === 'n') { ny = r0.y + dy; nh = r0.h - dy }
            else if (info.corner === 's') { nh = r0.h + dy }
            else if (info.corner === 'w') { nx = r0.x + dx; nw = r0.w - dx }
            else if (info.corner === 'e') { nw = r0.w + dx }

            if (info.constrainAspect) {
                const ratio = (r0.w || 1) / (r0.h || 1)
                const driveWidth = Math.abs(nw - r0.w) >= Math.abs(nh - r0.h)
                if (driveWidth) {
                    nh = nw / (ratio || 1)
                    if (info.corner === 'n' || info.corner === 'nw' || info.corner === 'ne') {
                        ny = r0.y + (r0.h - nh)
                    }
                } else {
                    nw = nh * ratio
                    if (info.corner === 'w' || info.corner === 'nw' || info.corner === 'sw') {
                        nx = r0.x + (r0.w - nw)
                    }
                }
            }
            if (info.resizeFromCenter) {
                const cx = r0.x + r0.w / 2
                const cy = r0.y + r0.h / 2
                nx = cx - nw / 2
                ny = cy - nh / 2
            }
            nw = Math.max(10, nw)
            nh = Math.max(10, nh)
            requestAnimationFrame(() => {
                frameEditHook.updateLayerRect(info.frameId, info.layerId, { x: nx, y: ny, w: nw, h: nh })
            })
            return true
        }
        return false
    }, [clientToWorld, frameEditHook])

    const onFramePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current
        if (frameResizeRef.current) {
            frameResizeRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        if (frameMoveRef.current) {
            frameMoveRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        if (layerMoveRef.current) {
            layerMoveRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        if (layerResizeRef.current) {
            layerResizeRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        return false
    }, [containerRef])

    return {
        frameItems,
        framingIds,
        selectedFrameId,
        activeLayerByFrameId,
        setActiveLayer,
        enableFrameFor,
        disableFrameFor,
        confirmFrameFor,
        revertFrameFor,
        resetFraming,
        commitLayoutPositions: frameEditHook.commitLayoutPositions,
        frameEditState: frameEditHook.frameEditState,
        frameEditStateRef: frameEditHook.frameEditStateRef,
        syncImageRect: frameEditHook.syncLayerRect, // Kept for potential external usage, mapped to syncLayerRect
        onFrameSelect: (id: string) => setSelectedFrameId(id),
        onFrameMoveStart: startMoveFrame,
        onFrameResizeStart: startResizeFrame,
        onLayerMove: startMoveLayer,
        onLayerResize: startResizeLayer,
        onFramePointerMove,
        onFramePointerUp,
        addLayer: frameEditHook.addLayer,
        reorderLayers: frameEditHook.reorderLayers,
        removeLayer: frameEditHook.removeLayer,
        replaceFrameEditState: frameEditHook.replaceFrameEditState,
    }
}

export default useFraming
